import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { RmIssue } from '../../settings-reminders/derive';

/**
 * The reminders editor shell (amendment 25 §1).
 *
 * **Two steps, not three** — `1 Messages · 2 Test and save`. The recall builder
 * has an audience step because a recall chooses who it is for; a reminder's
 * audience is *everyone with an appointment*, so a page asking who would have one
 * answer and no controls.
 *
 * These pages are **full width with no settings sub-menu**: the editor is the
 * whole task, and a 258px column of other settings beside it is an invitation to
 * leave halfway through.
 */

export type RmPageId = 'messages' | 'test';

const STEPS: { id: RmPageId; n: number; label: string }[] = [
  { id: 'messages', n: 1, label: 'Messages' },
  { id: 'test', n: 2, label: 'Test and save' },
];

export function RmEditorPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1500px] flex-1 overflow-y-auto px-8 pb-16 pt-6">
      {children}
    </div>
  );
}

export function RmEditorHeader({
  active,
  crumb,
  titleLead,
  titleAccent,
  sub,
  onGoTo,
}: {
  active: RmPageId;
  /** `Settings › Appointment Reminders › Before the visit`. */
  crumb: string;
  titleLead: string;
  titleAccent: string;
  sub: string;
  onGoTo?: (page: RmPageId) => void;
}) {
  const activeIndex = STEPS.findIndex((s) => s.id === active);
  return (
    <div>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">{crumb}</div>
      <h1 className="m-0 mt-1 text-[26px] font-bold leading-[1.15] tracking-[-0.025em] text-[var(--text-primary)]">
        {titleLead} <span className="text-[var(--brand-indigo)]">{titleAccent}</span>
      </h1>
      <p className="m-0 mt-1.5 max-w-[720px] text-[13px] leading-[1.6] text-[var(--text-muted)]">{sub}</p>

      <div className="mt-4 flex items-center gap-2.5">
        {STEPS.map((s, i) => {
          const done = i < activeIndex;
          const on = s.id === active;
          return (
            <React.Fragment key={s.id}>
              <button
                type="button"
                disabled={on}
                onClick={() => {
                  onGoTo?.(s.id);
                }}
                className={cn(
                  'inline-flex items-center gap-1.5',
                  !on ? 'cursor-pointer hover:underline' : 'cursor-default',
                )}
              >
                <span
                  className={cn(
                    'inline-flex h-[22px] w-[22px] items-center justify-center rounded-full font-[var(--font-mono)] text-[12.5px] font-bold',
                    on || done ? 'bg-[var(--brand-indigo)] text-white' : 'bg-[#F1F3F9] text-[var(--text-muted)]',
                  )}
                >
                  {done ? <Icon kind="check" size={12} stroke={3} /> : s.n}
                </span>
                <span
                  className={cn('text-[12.5px]', on ? 'font-bold text-[var(--text-primary)]' : 'text-[var(--text-muted)]')}
                >
                  {s.label}
                </span>
              </button>
              {i < STEPS.length - 1 ? <span className="h-px w-8 bg-[var(--border-default)]" /> : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export function RmFooter({ left, children }: { left?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
      <div className="min-w-0 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{left}</div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

export function RmButton({
  kind = 'ghost',
  disabled,
  onClick,
  title,
  children,
}: {
  kind?: 'primary' | 'ghost' | 'soft';
  disabled?: boolean;
  onClick?: () => void;
  title?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={cn(
        'rounded-[10px] px-4 py-2 text-[13.5px] font-semibold',
        kind === 'primary' && disabled !== true && 'bg-[var(--brand-indigo)] text-white',
        kind === 'primary' && disabled === true && 'cursor-not-allowed bg-[#B9BFD4] text-white',
        kind === 'soft' && 'bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]',
        kind === 'ghost' &&
          'border border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
      )}
    >
      {children}
    </button>
  );
}

/**
 * The fix list — **each issue links to the step that carries it**, which is the
 * bug spec 23 §4.2 found on the recall side: the links named a step and opened
 * step 1.
 */
export function RmFixList({
  issues,
  onGoToStep,
}: {
  issues: RmIssue[];
  onGoToStep?: (stepId: string) => void;
}) {
  if (issues.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--money-green)]">
        <Icon kind="check" size={13} stroke={3} />
        Every step is ready.
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col gap-1">
      {issues.map((issue, i) => (
        <button
          key={`${issue.stepId}-${String(i)}`}
          type="button"
          onClick={() => {
            onGoToStep?.(issue.stepId);
          }}
          className="text-left text-[12.5px] font-semibold text-[var(--alert-red)] hover:underline"
        >
          {issue.long} →
        </button>
      ))}
    </span>
  );
}

/** A section heading inside the editor — used for the complication card and such. */
export function RmCard({
  title,
  sub,
  children,
  tone = 'plain',
}: {
  title?: string;
  sub?: React.ReactNode;
  children?: React.ReactNode;
  tone?: 'plain' | 'red';
}) {
  return (
    <div
      className={cn(
        'rounded-[16px] border bg-[var(--surface-card)] p-4',
        tone === 'red'
          ? 'border-[color-mix(in_srgb,var(--alert-red)_28%,transparent)]'
          : 'border-[var(--border-default)]',
      )}
    >
      {title === undefined ? null : (
        <div className="text-[13px] font-bold text-[var(--text-primary)]">{title}</div>
      )}
      {sub === undefined ? null : (
        <p className="m-0 mt-1 max-w-[760px] text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{sub}</p>
      )}
      {children}
    </div>
  );
}
